import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError, getRole } from './modules/core/index.js'
import OperatorApp from './OperatorApp.js'
import WorkerApp from './WorkerApp.js'
import AdminApp from './admin/AdminApp.js'
import { demoSessionShowsEndpoints, isAwaitingDemoSessionHandoff } from './modules/accounts/index.js'
import { EndpointHintsProvider } from './modules/core/index.js'
import { DemoSessionHandoff } from './modules/accounts/index.js'
import { WebModulesProvider } from './modules/core/index.js'
import { REGISTRY } from './webModules.js'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // 4xx (not found, unauthorized, bad request, ...) won't succeed on
        // retry - e.g. an unconfigured facility's 404 was costing ~7s of
        // exponential-backoff retries before FacilityNotConfigured ever
        // rendered. Only retry genuinely transient failures (network, 5xx).
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element not found')
}

// A signed-in session picks its own shell - a worker who signed in at `/`
// lands in the worker shell, an operator who opened `/worker` lands in the
// operator one - and only a fresh visit goes by the path.
// A path typed with capitals (`/ADMIN`, `/Worker`) is the same place: the first
// segment chooses the shell, so it is lowercased in the address before anything
// reads it. Later segments are left as typed, since a facility's id is its own.
const [, firstSegment = '', ...laterSegments] = window.location.pathname.split('/')
if (firstSegment !== firstSegment.toLowerCase()) {
  window.history.replaceState(window.history.state, '', `/${[firstSegment.toLowerCase(), ...laterSegments].join('/')}${window.location.search}${window.location.hash}`)
}
const storedRole = getRole()
const isWorkerRoute = storedRole ? storedRole === 'worker' : window.location.pathname.startsWith('/worker')
// SeeWhoCares staff only - a separate shell with its own sign-in (the shared
// platform-admin credential), sharing nothing with the operator session.
const isAdminRoute = /^\/admin(\/|$)/.test(window.location.pathname)
// A tab opened to receive a demo session (api/demoSession.ts) shows the
// hand-off screen until the session arrives, then reloads signed in.
const isDemoSessionHandoff = !isAdminRoute && isAwaitingDemoSessionHandoff()

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <WebModulesProvider registry={REGISTRY}>
      {/* The API-calls inspector is the admin shell's, shown to a signed-in admin only; every shell's calls are still recorded for it. */}
      {/* Endpoint paths are an admin's view too. The admin shell switches them on for itself; here they are on for a demo session an admin opened, so the admin sees them while looking as an operator or worker. */}
      {isDemoSessionHandoff ? (
        <DemoSessionHandoff />
      ) : isAdminRoute ? (
        <AdminApp />
      ) : (
        <EndpointHintsProvider value={demoSessionShowsEndpoints()}>{isWorkerRoute ? <WorkerApp /> : <OperatorApp />}</EndpointHintsProvider>
      )}
      </WebModulesProvider>
    </QueryClientProvider>
  </StrictMode>,
)
